import type { FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useMemo } from 'react'
import { useEntityHistory } from '../useEntityHistory/index'
import { useError } from '../useError/index'
import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import type { Activity, FilterQuery, History } from '@ztechapps/common.contract'
import { gql } from '@apollo/client'

export const useActivityRemarksAndHistory = ({
  remarksActivity = [],
  historyFilter,
  component,
  showHistory,
}: {
  remarksActivity: Activity[]
  historyFilter?: FilterQuery<History>
  component: FunctionComponent<{ item: History }>
  showHistory?: boolean
}) => {
  const { list, error, subscribeToMore } = useEntityHistory({
    filter: historyFilter,
    limit: 0,
    options: { queryOptions: { fetchPolicy: 'network-only', skip: !showHistory || !historyFilter } },
  })

  // A failed history load raises one error toast; the remarks keep rendering.
  useError(error)

  useEffect(() => {
    if (!showHistory) return

    const unsubscribe = subscribeToMore({
      document: gql(`
          subscription createdHistory($filter: JSON) {
            createdHistory(filter: $filter) {
              _id
              entity
              entityKey
              timeStamp
              description
              author
              diff
            }
          }
        `),
      variables: { filter: historyFilter },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData?.data) return prev

        const newHistory = subscriptionData.data.createdHistory

        return {
          ...prev,
          allHistory: [...prev.allHistory, newHistory],
        }
      },
    })

    // tear down the previous subscription before a re-run resubscribes, so re-runs replace
    // the live subscription instead of stacking another one on top
    return unsubscribe
    // historyFilter intentionally excluded: every call site builds it as a new object literal on
    // each render (e.g. `const historyFilter = { $and: [...] }` in RemarksActivity.tsx), so it is
    // never referentially stable. Cleanup now makes re-subscribing safe, but including
    // `historyFilter` here would fire a teardown/resubscribe on every unrelated re-render of the
    // consuming component, not just on a real filter change — that is a new cost, not the stacking
    // bug this comment used to describe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToMore, showHistory])

  const historyList = filter(list, (item) => {
    const parsedDiff = JSON.parse(item.diff)
    if (parsedDiff?.length === 1 && parsedDiff[0]?.fieldName === 'viewCount') return false
    const noRemarksField = find(parsedDiff, (diff) => !diff.fieldName.startsWith('remarks'))

    if (isEmpty(noRemarksField)) return false

    return true
  })

  //render the activity as a react component
  const historyListAsActivity = map(historyList, (item) => {
    return {
      _id: item._id,
      activity: component({ item }),
      updatedAt: item.timeStamp,
      isDeleted: false,
    }
  }) as unknown as Activity[]

  const activities = useMemo(
    () => [...(remarksActivity ?? []), ...(historyListAsActivity ?? [])],
    [remarksActivity, historyListAsActivity]
  )

  return { activities }
}
